import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  Divider,
} from "@mui/material";
import {
  RocketLaunchOutlined as RocketIcon,
  Shuffle as ShuffleIcon,
  RestartAlt as ClearIcon,
  BackspaceOutlined as BackspaceIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../../theme";
import { CommonButton, CommonChip } from "../../../../Components/ui";

// Missile tier assets
import HypersonicImg from "../../../../../static/Images/Hypersonic.png";
import BallasticImg from "../../../../../static/Images/Ballastic.png";
import CruisImg from "../../../../../static/Images/cruis.png";
import InitiationImg from "../../../../../static/Images/Initiation.png";

const MISSILE_TIERS = {
  1: {
    name: "Hypersonic Missile",
    img: HypersonicImg,
    badge: "Hypersonic Tier",
    color: colors.amber[600],
    bg: colors.amber[50],
  },
  2: {
    name: "Ballistic Missile",
    img: BallasticImg,
    badge: "Ballistic Tier",
    color: colors.red[600],
    bg: colors.red[50],
  },
  3: {
    name: "Cruise Missile",
    img: CruisImg,
    badge: "Cruise Tier",
    color: colors.blue[600],
    bg: colors.blue[50],
  },
  4: {
    name: "Initiation Missile",
    img: InitiationImg,
    badge: "Initiation Tier",
    color: colors.emerald[600],
    bg: colors.emerald[50],
  },
};

const MissilePicker = ({
  level = 4,
  pickNumbers = 3,
  selectedNumber = "",
  onSelectNumber,
  onQuickPick,
  onClear,
}) => {
  const currentLevel = Number(level) || 4;
  const tierInfo = MISSILE_TIERS[currentLevel] || MISSILE_TIERS[4];
  const maxDigits = Math.max(1, Number(pickNumbers) || 3);

  const handleKeypadPress = (val) => {
    if (val === "DELETE") {
      onSelectNumber(selectedNumber.toString().slice(0, -1));
    } else if (val === "CLEAR") {
      onClear();
    } else {
      if (selectedNumber.toString().length < maxDigits) {
        onSelectNumber(`${selectedNumber}${val}`);
      }
    }
  };

  const keypadKeys = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
    ["CLEAR", 0, "DELETE"],
  ];

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.lg,
        border: `1px solid ${colors.slate[200]}`,
        boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
        bgcolor: "#ffffff",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        {/* Header Section */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: colors.slate[900],
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <RocketIcon sx={{ color: colors.amber[600] }} />
              Target Selection Deck
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: colors.slate[500], mt: 0.25 }}
            >
              Enter up to <strong>{maxDigits} digits</strong> for your rocket
              launch trajectory.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <CommonButton
              variant="amberAction"
              size="small"
              startIcon={<ShuffleIcon fontSize="small" />}
              onClick={onQuickPick}
              label="Random Target"
            />
            <CommonButton
              variant="clearAction"
              size="small"
              startIcon={<ClearIcon fontSize="small" />}
              onClick={onClear}
              disabled={!selectedNumber}
              label="Clear"
            />
          </Stack>
        </Stack>

        <Grid container spacing={3} alignItems="center">
          {/* Left: Rocket Graphic & Tier Showcase */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: colors.slate[900],
                borderRadius: borderRadiusPx.lg,
                p: 3,
                textAlign: "center",
                color: "#ffffff",
                position: "relative",
                overflow: "hidden",
                border: `1px solid ${colors.slate[700]}`,
                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.2)",
              }}
            >
              <CommonChip
                label={tierInfo.badge}
                variant="warning"
                size="small"
                sx={{ mb: 2, fontWeight: 700 }}
              />

              <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                {tierInfo.name}
              </Typography>

              <Box
                sx={{
                  my: 2,
                  height: 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={tierInfo.img}
                  alt={tierInfo.name}
                  className="missile-preview-image"
                />
              </Box>

              <Typography
                variant="caption"
                sx={{ color: colors.slate[400], display: "block" }}
              >
                Multi-stage verifiable blockchain trajectory
              </Typography>
            </Box>
          </Grid>

          {/* Right: Target Screen & Keypad */}
          <Grid item xs={12} md={6}>
            {/* Target Display Screen */}
            <Box className="missile-target-display" sx={{ mb: 2.5 }}>
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[400],
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  display: "block",
                  mb: 0.5,
                }}
              >
                Selected Target Number
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: selectedNumber ? "#ffffff" : colors.slate[600],
                  fontFamily: "monospace",
                  letterSpacing: 4,
                  minHeight: "48px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {selectedNumber || "---"}
              </Typography>
            </Box>

            {/* Keypad */}
            <div className="missile-keypad-grid">
              {keypadKeys.map((row, rIdx) =>
                row.map((k, cIdx) => (
                  <div
                    key={`${rIdx}-${cIdx}`}
                    className={`missile-keypad-btn ${
                      typeof k === "string" ? "action-btn" : ""
                    }`}
                    onClick={() => handleKeypadPress(k)}
                    role="button"
                    tabIndex={0}
                  >
                    {k === "DELETE" ? (
                      <BackspaceIcon fontSize="small" />
                    ) : k === "CLEAR" ? (
                      "Clear"
                    ) : (
                      k
                    )}
                  </div>
                )),
              )}
            </div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default MissilePicker;
